import React, { useState } from "react";
import { api, formatError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Megaphone, Coins, CheckCircle2, Target } from "lucide-react";

export default function Advertise() {
  const [form, setForm] = useState({
    advertiser_name: "", advertiser_email: "", headline: "", body: "",
    image_url: "", click_url: "", category: "general", budget_usd: 100,
  });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/ads/apply", { ...form, budget_usd: parseFloat(form.budget_usd) });
      setDone(true);
      toast.success("Ad submitted for review");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="advertise-success" className="max-w-xl mx-auto px-4 py-20 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#2F7D32] mx-auto" />
        <h1 className="font-serif text-4xl text-[#4A0E17] mt-5">Ad submitted</h1>
        <p className="text-[#63534E] mt-4">We'll review your creative, check brand-safety, and switch it live within 12 hours.</p>
      </div>
    );
  }

  return (
    <div data-testid="advertise-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <Megaphone className="w-10 h-10 text-[#E65100]" />
      <div className="overline mt-4">Advertise on Pancham Dham</div>
      <h1 className="font-serif text-5xl text-[#4A0E17] mt-2 leading-tight">Reach devotees who care</h1>
      <p className="text-[#63534E] mt-5 text-lg max-w-2xl leading-relaxed">
        Promote your products — rudraksha, pooja supplies, yatra services, spiritual books, Ayurvedic goods — directly to engaged devotees. <strong className="text-[#E65100]">Viewers earn a small reward</strong> for each ad they see, so your impressions always find attention.
      </p>

      <div className="mt-10 grid md:grid-cols-3 gap-5">
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <Target className="w-7 h-7 text-[#E65100]" />
          <div className="font-serif text-lg text-[#4A0E17] mt-4">Engaged audience</div>
          <p className="text-sm text-[#63534E] mt-2">Pan-India + diaspora devotees of Shiva. No bots, no filler.</p>
        </div>
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <Coins className="w-7 h-7 text-[#E65100]" />
          <div className="font-serif text-lg text-[#4A0E17] mt-4">$2 CPM, viewers earn 70%</div>
          <p className="text-sm text-[#63534E] mt-2">Fair price per thousand impressions. 70% flows to viewer wallets, 30% to platform.</p>
        </div>
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <Megaphone className="w-7 h-7 text-[#E65100]" />
          <div className="font-serif text-lg text-[#4A0E17] mt-4">Brand-safe only</div>
          <p className="text-sm text-[#63534E] mt-2">Human review before every ad goes live. Nothing anti-devotional.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-10 bg-[#FFFDF9] sacred-border rounded-3xl p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="ad-advertiser" required placeholder="Company / brand name" value={form.advertiser_name} onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="ad-email" required type="email" placeholder="Contact email" value={form.advertiser_email} onChange={(e) => setForm({ ...form, advertiser_email: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
        </div>
        <Input data-testid="ad-headline" required placeholder="Ad headline (max 90 chars)" maxLength={90} value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
        <Textarea data-testid="ad-body" rows={3} maxLength={240} placeholder="Short body copy (max 240 chars)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="bg-white border-[#D4AF37]/40" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="ad-image" required placeholder="Image URL (16:9 recommended)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="ad-click" required placeholder="Click-through URL" value={form.click_url} onChange={(e) => setForm({ ...form, click_url: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input data-testid="ad-category" placeholder="Category (e.g. rudraksha, yatra)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          <Input data-testid="ad-budget" required type="number" min="10" step="10" placeholder="Budget (USD, min $10)" value={form.budget_usd} onChange={(e) => setForm({ ...form, budget_usd: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
        </div>
        <div className="text-xs text-[#63534E]">Estimated impressions at $2 CPM: <strong className="text-[#4A0E17]">{Math.round((parseFloat(form.budget_usd) || 0) * 500)}</strong> ad views</div>
        <Button data-testid="ad-submit" type="submit" disabled={submitting} className="w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-12 text-base">
          {submitting ? "Submitting…" : "Submit ad for review"}
        </Button>
      </form>
    </div>
  );
}
